import { useEventListener } from 'usehooks-ts';
import { useRef } from 'react';
import type { Locale } from '../../../utils/locale';
import { useMeta } from './use-meta';

export interface WebSDKElement extends Element {
  setToken: (token: string) => void;
}

interface DesktopEvents {
  onOnboardingStarted?: (event: CustomEvent<'OnboardingStarted'>) => void;
  onLoaded?: (event: CustomEvent<'Loaded'>) => void;
  onOnboardingContinuing?: (event: CustomEvent<'OnboardingContinuing'>) => void;
  onOnboardingCompleted?: (event: CustomEvent<'OnboardingCompleted'>) => void;
  onOnboardingCompletedError?: (event: CustomEvent<'OnboardingCompletedError'>) => void;
  onOnboardingRestartRequest: (webSdkElement: WebSDKElement) => Promise<void>;
}

export type ContinueMobileRequestEvent = CustomEvent<
  (redirectHandlerArgs: { redirect: string; redirectFailure: string }) => void
>;

interface MobileEvents {
  onContinueMobileRequest?: (event: ContinueMobileRequestEvent) => void;
}

interface Events {
  desktop: DesktopEvents;
  mobile?: MobileEvents;
}

export interface EmbeddedWebsdkProps {
  token: string;
  locale: Locale;
  visible: boolean;
  events: Events;
  disclaimer?: React.ReactNode;
  eventStartedProperties?: Record<string, unknown>;
}

declare global {
  interface DocumentEventMap {
    flOnboardingRestartRequest: CustomEvent;
    flOnboardingStatus: CustomEvent<
      | 'OnboardingStarted'
      | 'Loaded'
      | 'OnboardingContinuing'
      | 'OnboardingCompleted'
      | 'OnboardingCompletedError'
    >;
    flContinueMobileRequest: ContinueMobileRequestEvent;
  }

  // eslint-disable-next-line @typescript-eslint/no-namespace -- Allow extending IntrinsicElements to recognize the custom element
  namespace JSX {
    interface IntrinsicElements {
      'fl-flow-onboarding': {
        token: string;
        locale: string;
      };
    }
  }
}

export function EmbeddedWebsdk({
  token,
  locale,
  visible,
  events,
  disclaimer,
}: EmbeddedWebsdkProps): JSX.Element {
  useMeta();
  const documentRef = useRef<Document>(document);

  useEventListener(
    'flOnboardingStatus',
    event => {
      if (event.detail === 'OnboardingStarted') {
        events.desktop.onOnboardingStarted?.(event as CustomEvent<'OnboardingStarted'>);
      } else if (event.detail === 'Loaded') {
        events.desktop.onLoaded?.(event as CustomEvent<'Loaded'>);
      } else if (event.detail === 'OnboardingContinuing') {
        events.desktop.onOnboardingContinuing?.(event as CustomEvent<'OnboardingContinuing'>);
      } else if (event.detail === 'OnboardingCompleted') {
        events.desktop.onOnboardingCompleted?.(event as CustomEvent<'OnboardingCompleted'>);
      } else {
        // event.detail === 'OnboardingCompletedError'
        events.desktop.onOnboardingCompletedError?.(
          event as CustomEvent<'OnboardingCompletedError'>
        );
      }
    },
    documentRef
  );

  useEventListener(
    'flOnboardingRestartRequest',
    (): void => {
      const element = document.querySelector('fl-flow-onboarding');
      if (element) {
        void events.desktop.onOnboardingRestartRequest(element as WebSDKElement);
      }
    },
    documentRef
  );

  useEventListener(
    'flContinueMobileRequest',
    event => {
      events.mobile?.onContinueMobileRequest?.(event);
    },
    documentRef
  );

  return (
    <div data-test-fourthline-embedded-websdk style={{ display: visible ? 'block' : 'none' }}>
      <fl-flow-onboarding locale={locale} token={token} />
      {disclaimer}
    </div>
  );
}
